import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { FileText, SignIn, WarningCircle } from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Connexion réussie");
      const next = location.state?.from || "/dashboard";
      navigate(next, { replace: true });
    } catch (err) {
      const msg = err.response?.data?.detail || "Identifiants invalides";
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <Link to="/portail" className="flex items-center gap-2.5 mb-8 hover:opacity-80" data-testid="login-logo-home">
          <img src="/logo-ecocasse.png" alt="Ecocasse" className="w-10 h-10 object-contain" />
          <div>
            <div className="font-heading font-semibold text-lg leading-none">Portail d'activité</div>
            <div className="label-tiny text-zinc-500 mt-1">Ecocasse</div>
          </div>
        </Link>

        <div className="bg-white border border-zinc-200 p-7 sm:p-8">
          <h1 className="font-heading text-2xl font-semibold tracking-tight mb-1">Connexion back-office</h1>
          <p className="text-sm text-zinc-500 mb-6">Accès réservé aux collaborateurs Ecocasse.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            {errorMsg && (
              <div
                className="flex items-start gap-2 border border-red-200 bg-red-50 text-red-800 text-sm px-3 py-2.5"
                role="alert"
                data-testid="login-error"
              >
                <WarningCircle size={16} weight="fill" className="text-red-600 mt-0.5 shrink-0" />
                <span>{errorMsg}</span>
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="label-tiny text-zinc-500">E-mail</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@ecocasse.fr"
                className="h-10 rounded-sm border-zinc-300"
                required
                autoFocus
                data-testid="login-email"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="label-tiny text-zinc-500">Mot de passe</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-10 rounded-sm border-zinc-300"
                required
                data-testid="login-password"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm h-11 gap-2"
              data-testid="login-submit"
            >
              <SignIn size={16} weight="bold" />
              {loading ? "Connexion..." : "Se connecter"}
            </Button>
          </form>
        </div>

        <div className="text-xs text-center text-zinc-500 mt-4">
          <Link to="/portail" className="hover:text-zinc-900 underline">← Retour au portail client</Link>
        </div>
      </div>
      <Toaster position="top-right" richColors />
    </div>
  );
}
